// MODALITÀ NEGOZIO: il negozio personale dell'artigiano come sito a sé, dentro Rarifex.
// Intestazione e piè di pagina della bottega, pagine proprie, schede prodotto senza
// altri artigiani. Anche carrello, pagamento, accesso, conferma ordine, messaggi e
// pagine informative (motore Rarifex) si vedono qui dentro, con la grafica della bottega.
import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { fetchMyShop, fetchPublicShop } from '../lib/shopsApi';
import { enterShop, exitShop, requestAttribution, shopPaths } from '../lib/shopSession';
import {
  SHOP_MENU_ITEMS, SHOP_PAGES, ensureShopFont, layoutClasses, normalizeMenu, shopModeStyle, shopThemeStyle,
} from '../lib/shopThemes';
import { setJsonLd, setPageSeo } from '../utils/urls';
import { shopPageSeo } from '../lib/shopSeo';
import { getShopCache, setShopCache } from '../lib/shopCache';
import { rememberShopEntry, trackShopPath } from '../lib/shopTracking';
import DirectRequestDialog from '../components/DirectRequestDialog';
import ProductDetailPage from '../pages/ProductDetailPage';
import CartPage from '../pages/CartPage';
import LoginPage from '../pages/LoginPage';
import SignupPage from '../pages/SignupPage';
import OAuthCallbackPage from '../pages/OAuthCallbackPage';
import EmailConfirmedPage from '../pages/EmailConfirmedPage';
import CheckEmailPage from '../pages/CheckEmailPage';
import OrderSuccessPage from '../pages/OrderSuccessPage';
import MessagesPage from '../pages/MessagesPage';
import OrderCasePage from '../pages/OrderCasePage';
import ShippingPage from '../pages/ShippingPage';
import ReturnsPage from '../pages/ReturnsPage';
import PaymentsPage from '../pages/PaymentsPage';
import HowToBuyPage from '../pages/HowToBuyPage';
import FAQPage from '../pages/FAQPage';
import TermsPage from '../pages/TermsPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import CookiePolicyPage from '../pages/CookiePolicyPage';
import { ShopContext } from './ShopContext';
import ShopHeader from './ShopHeader';
import ShopFooter from './ShopFooter';
import ShopHome from './pages/ShopHome';
import ShopProducts from './pages/ShopProducts';
import ShopCustom from './pages/ShopCustom';
import ShopAbout from './pages/ShopAbout';
import ShopPortfolio from './pages/ShopPortfolio';
import ShopReviews from './pages/ShopReviews';
import ShopContact from './pages/ShopContact';
import ShopShipping from './pages/ShopShipping';
import ShopCollections, { ShopCollection } from './pages/ShopCollections';

const NAV_LABELS = { social: 'Contatti' };
const PREVIEW_KEY = 'rarifex_shop_preview';

const readPreviewFlag = (slug) => {
  try { return sessionStorage.getItem(PREVIEW_KEY) === slug; } catch (_) { return false; }
};
const writePreviewFlag = (slug) => {
  try { sessionStorage.setItem(PREVIEW_KEY, slug); } catch (_) { /* niente */ }
};

async function loadShop(slug, { preview, userId }) {
  const { data: crafter } = await supabase.from('profiles').select('*').eq('slug', slug).maybeSingle();
  if (!crafter) return { error: 'not_found' };
  if (crafter.deleted_at || crafter.storefront_disabled) return { error: 'unavailable', crafter };

  let shop;
  let isPreview = false;
  if (preview && userId === crafter.id) {
    try {
      const mine = await fetchMyShop();
      shop = { active: !!mine.can_customize, ...mine };
      isPreview = true;
    } catch (_) {
      shop = await fetchPublicShop(crafter.id);
    }
  } else {
    shop = await fetchPublicShop(crafter.id);
  }
  if (!shop.active) return { inactive: true, crafter };

  const [{ data: products }, { data: portfolio }, { data: reviews }] = await Promise.all([
    supabase.from('products').select('*').eq('crafter_id', crafter.id).eq('status', 'active').order('created_at', { ascending: false }),
    supabase.from('portfolio_items').select('*').eq('crafter_id', crafter.id).order('created_at', { ascending: false }),
    supabase.from('reviews').select('rating, comment, created_at, product_id, client:client_id(full_name)').eq('crafter_id', crafter.id).order('created_at', { ascending: false }),
  ]);

  const productReviews = {};
  (reviews || []).forEach((r) => {
    if (!r.product_id) return;
    const e = productReviews[r.product_id] || { total: 0, count: 0 };
    e.total += r.rating;
    e.count += 1;
    e.average = e.total / e.count;
    productReviews[r.product_id] = e;
  });
  const reviewCount = (reviews || []).length;
  const averageRating = reviewCount ? (reviews || []).reduce((s, r) => s + r.rating, 0) / reviewCount : 0;

  return {
    crafter,
    shop,
    preview: isPreview,
    products: products || [],
    portfolio: portfolio || [],
    reviews: reviews || [],
    productReviews,
    reviewStats: { averageRating, reviewCount },
  };
}

export default function ShopApp({ slug, embedded = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [requestOpen, setRequestOpen] = useState(false);

  const params = new URLSearchParams(location.search);
  if (params.get('anteprima') === '1') writePreviewFlag(slug);
  const wantsPreview = params.get('anteprima') === '1' || readPreviewFlag(slug);
  const cacheKey = `${slug}|${wantsPreview && user?.id ? 'p' : 'pub'}`;
  const cached = getShopCache(cacheKey);
  const [state, setState] = useState(cached ? { loading: false, ...cached } : { loading: true });

  // Ingresso nel negozio: link della bottega (?ref) o pulsante "Entra nel negozio" (?negozio=1)
  useEffect(() => {
    if (embedded) return;
    const p = new URLSearchParams(location.search);
    const ref = (p.get('ref') || '').toLowerCase();
    enterShop(slug);
    rememberShopEntry(slug, p); // provenienza della visita per le statistiche
    if (ref && ref === slug.toLowerCase()) requestAttribution(slug);
    if (p.has('ref') || p.has('negozio') || p.has('via')) {
      p.delete('ref');
      p.delete('negozio');
      p.delete('via');
      const qs = p.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    let active = true;
    const fresh = getShopCache(cacheKey);
    if (fresh) {
      setState({ loading: false, ...fresh });
      return undefined;
    }
    (async () => {
      const data = await loadShop(slug, { preview: wantsPreview, userId: user?.id });
      if (!active) return;
      if (data.inactive) {
        // Vetrina avanzata non attiva: si torna alla vetrina normale (o alla pagina Rarifex)
        exitShop();
        if (embedded) navigate(`${location.pathname}${location.search}`, { replace: true });
        else navigate(`/artigiano/${slug}${wantsPreview ? '?anteprima=1' : ''}`, { replace: true });
        return;
      }
      if (!data.error) setShopCache(cacheKey, data);
      setState({ loading: false, ...data });
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const ctx = useMemo(() => {
    if (!state.crafter || !state.shop) return null;
    const { crafter, shop, products, portfolio } = state;
    const branding = shop.branding;
    const menu = normalizeMenu(shop.menu).filter((m) => m.enabled);
    const hasSocial = crafter.social_links && Object.values(crafter.social_links).some((v) => v);
    // Collezioni visibili con almeno una creazione attiva, nell'ordine scelto
    const collections = (shop.collections || [])
      .filter((c) => c.visible !== false)
      .map((c) => {
        const items = (c.product_ids || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
        return { ...c, products: items, cover: products.find((p) => p.id === c.cover_product_id) || items[0] };
      })
      .filter((c) => c.products.length > 0);
    const navItems = menu
      .filter((m) => (m.key === 'portfolio' ? portfolio.length > 0 : m.key === 'collezioni' ? collections.length > 0 : true))
      .map((m) => ({
        key: m.key,
        label: m.key === 'laboratorio'
          ? (crafter.storefront_section_name || SHOP_MENU_ITEMS[m.key].label)
          : (NAV_LABELS[m.key] || SHOP_MENU_ITEMS[m.key].label),
        to: shopPaths(slug).page(SHOP_PAGES[m.key]),
      }));
    const featured = (shop.featured_product_ids || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
    return {
      ...state,
      slug,
      embedded,
      name: crafter.storefront_title || crafter.full_name || 'Bottega',
      branding,
      menuKeys: menu.map((m) => m.key),
      navItems,
      hasSocial,
      featured,
      collections,
      paths: shopPaths(slug),
      style: shopModeStyle(branding.layout),
      layout: layoutClasses(branding.layout),
      openRequest: () => setRequestOpen(true),
    };
  }, [state, slug, embedded]);

  // Statistiche del negozio: visite e pagine viste (mai in anteprima né dall'artigiano stesso)
  const trackCrafterId = state.crafter?.id;
  const trackOff = !ctx || ctx.preview || authLoading || user?.id === trackCrafterId;
  useEffect(() => {
    if (trackOff) return;
    trackShopPath({ crafterId: trackCrafterId, slug, pathname: location.pathname, embedded, products: state.products });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, trackOff, trackCrafterId]);

  // Carattere tipografico del modello scelto (caricato solo quando serve)
  useEffect(() => {
    if (ctx) ensureShopFont(ctx.branding.layout);
  }, [ctx]);

  // SEO per pagina: titolo, canonical e dati strutturati (la scheda prodotto gestisce i propri)
  useEffect(() => {
    if (!ctx || embedded) return;
    const seo = shopPageSeo(ctx, location.pathname);
    if (!seo) {
      ['shop', 'breadcrumb', 'page'].forEach((k) => setJsonLd(k, null));
      return;
    }
    setPageSeo(seo);
    setJsonLd('shop', seo.jsonLd.store);
    setJsonLd('breadcrumb', seo.jsonLd.breadcrumb);
    setJsonLd('page', seo.jsonLd.page);
  }, [ctx, embedded, location.pathname]);
  useEffect(() => () => ['shop', 'breadcrumb', 'page'].forEach((k) => setJsonLd(k, null)), []);

  if (state.loading) return <ShopSkeleton />;

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">
            {state.error === 'unavailable' ? 'Bottega non disponibile' : 'Bottega non trovata'}
          </h1>
          <p className="text-slate-500 mb-4">Scopri le altre botteghe artigiane su Rarifex.</p>
          <Link to="/crafters" onClick={exitShop} className="underline">Vai a Rarifex</Link>
        </div>
      </div>
    );
  }

  const menuKeys = ctx.menuKeys;
  const pageFor = (key, el) => (menuKeys.includes(key) && (key !== 'collezioni' || ctx.collections.length)
    ? el : <Navigate to={ctx.paths.home} replace />);

  return (
    <ShopContext.Provider value={ctx}>
      <div className={`min-h-screen flex flex-col bg-shop-bg ${ctx.style.font}`} style={shopThemeStyle(ctx.branding)} data-testid="shop-mode" data-shop-layout={ctx.style.id} data-shop-embedded={embedded ? 'true' : undefined}>
        {ctx.preview && (
          <div className="bg-slate-900 text-white text-xs md:text-sm" data-testid="shop-preview-banner">
            <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
              <span>
                Anteprima del tuo negozio{ctx.shop.is_published ? '' : ' — non ancora pubblicato: i clienti vedono la vetrina base'}
              </span>
              <Link to="/dashboard?tab=artigiano&section=storefront" className="underline underline-offset-2">Torna all'editor</Link>
            </div>
          </div>
        )}
        <ShopHeader />
        <main className="flex-1">
          {/* Dissolvenza in entrata a ogni cambio pagina. Niente animazione di uscita:
              la pagina uscente, rimontata sotto un'altra rotta, finirebbe sul fallback "*". */}
          <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
            >
              {embedded ? (
                // Pagine Rarifex incorporate: stesso indirizzo, grafica della bottega
                // (percorsi relativi: questa è una <Routes> discendente della rotta "*")
                <Routes location={location}>
                  <Route path="cart" element={<CartPage />} />
                  <Route path="order-success" element={<OrderSuccessPage />} />
                  <Route path="login" element={<LoginPage />} />
                  <Route path="signup" element={<SignupPage />} />
                  <Route path="auth/callback/:provider" element={<OAuthCallbackPage />} />
                  <Route path="auth/email-confirmed" element={<EmailConfirmedPage />} />
                  <Route path="auth/check-email" element={<CheckEmailPage />} />
                  <Route path="messages" element={<MessagesPage />} />
                  <Route path="messages/:conversationId" element={<MessagesPage />} />
                  <Route path="assistenza-ordine/:caseId" element={<OrderCasePage />} />
                  <Route path="spedizioni" element={<ShippingPage />} />
                  <Route path="resi-rimborsi" element={<ReturnsPage />} />
                  <Route path="pagamenti" element={<PaymentsPage />} />
                  <Route path="come-acquistare" element={<HowToBuyPage />} />
                  <Route path="faq" element={<FAQPage />} />
                  <Route path="termini" element={<TermsPage />} />
                  <Route path="privacy" element={<PrivacyPolicyPage />} />
                  <Route path="cookie" element={<CookiePolicyPage />} />
                  <Route path="*" element={<Navigate to={ctx.paths.home} replace />} />
                </Routes>
              ) : (
                <Routes location={location}>
                  <Route index element={<ShopHome />} />
                  <Route path="prodotti" element={pageFor('prodotti', <ShopProducts />)} />
                  <Route path="collezioni" element={pageFor('collezioni', <ShopCollections />)} />
                  <Route path="collezioni/:collectionSlug" element={pageFor('collezioni', <ShopCollection />)} />
                  <Route path="su-misura" element={pageFor('su_misura', <ShopCustom />)} />
                  <Route path="laboratorio" element={pageFor('laboratorio', <ShopAbout />)} />
                  <Route path="portfolio" element={pageFor('portfolio', <ShopPortfolio />)} />
                  <Route path="recensioni" element={pageFor('recensioni', <ShopReviews />)} />
                  <Route path="contatti" element={pageFor('social', <ShopContact />)} />
                  <Route path="spedizioni-resi" element={<ShopShipping />} />
                  <Route path="prodotto/:slug" element={<ProductDetailPage />} />
                  <Route path="*" element={<Navigate to={ctx.paths.home} replace />} />
                </Routes>
              )}
            </motion.div>
        </main>
        <ShopFooter />
        <DirectRequestDialog crafter={ctx.crafter} open={requestOpen} onOpenChange={setRequestOpen} />
      </div>
    </ShopContext.Provider>
  );
}

// Scheletro di caricamento (al posto della rotellina)
function ShopSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 animate-pulse" data-testid="shop-skeleton">
      <div className="h-20 bg-white border-b" />
      <div className="h-[50vh] bg-slate-200" />
      <div className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <div className="aspect-square rounded-xl bg-slate-200" />
            <div className="h-4 w-2/3 mt-3 rounded bg-slate-200" />
          </div>
        ))}
      </div>
    </div>
  );
}
